import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Drawer, Col, Row, DrawerProps, Radio } from 'antd';
import { useTranslation } from 'react-i18next';
import StatCard from '@components/StatCard';
import CoverageMap from '@components/Maps/CoverageMap';
import { AiOutlineHome, AiOutlineFieldNumber } from 'react-icons/ai';
import { VscGlobe } from 'react-icons/vsc';
import { TbAsterisk } from 'react-icons/tb';
import { FiPhoneCall } from 'react-icons/fi';
import './styles.less';
import { ClockCircleOutlined } from '@ant-design/icons';
import { dateFormat } from '@utils/formatters';

export interface ISessionLocationData {
  apn?: string;
  antenna?: string;
  cep?: string;
  city?: string;
  consumption?: string | number;
  imei?: string;
  imsi?: string;
  ip?: string;
  latitude?: number;
  longitude?: number;
  msisdn?: string;
  operator?: string;
  outputBytes?: string | number;
  sessionTime?: string;
  startTime?: string;
  state?: string;
  stopTime?: string;
  technology?: string;
  updateTime?: string;
  stoptime?: string;
  starttime?: string;
  uniqueid?: string;
}

interface ISessionLocation extends DrawerProps {
  isOpen?: boolean;
  dataArray?: ISessionLocationData[];
  onlyOneData?: ISessionLocationData;
}

const SessionLocationArray = ({
  isOpen,
  onClose,
  dataArray,
  onlyOneData,
}: ISessionLocation) => {
  const { t } = useTranslation('sessions');

  const firstDate = useMemo(
    () => (dataArray && dataArray[0])?.uniqueid || undefined,
    [dataArray],
  );

  const [activateDate, setActivateDate] = useState(firstDate);

  const selectedCityData = useMemo(
    () =>
      onlyOneData || dataArray?.find(item => item.uniqueid === activateDate),
    [activateDate, dataArray, onlyOneData],
  );

  const allStats = useCallback(
    (locationData?: ISessionLocationData) => [
      [
        {
          title: t('location.city'),
          value: `${locationData?.city ?? '-'} - ${locationData?.state ?? '-'}`,
          icon: <AiOutlineHome />,
        },
        {
          title: t('location.operator'),
          value: locationData?.operator,
          icon: <FiPhoneCall />,
        },
      ],
      [
        {
          title: t('location.antenna'),
          value: locationData?.antenna,
          icon: <span>0x</span>,
        },
        {
          title: (
            <>
              {t('location.coordinates')}
              <span style={{ fontSize: '0.75rem', marginLeft: '4px' }}>
                {`(${t('location.antenna')})`}
              </span>
            </>
          ),
          value: `${locationData?.latitude ?? '-'}° , ${
            locationData?.longitude ?? '-'
          }°`,
          icon: <VscGlobe />,
        },
      ],
      [
        {
          title: t('location.technology'),
          value: locationData?.technology,
          icon: <TbAsterisk />,
        },
        {
          title: t('location.msisdn'),
          value: locationData?.msisdn,
          icon: <AiOutlineFieldNumber />,
        },
      ],
      [
        {
          title: t('location.startTime'),
          value: dateFormat(locationData?.starttime || '').split(' ')[1],
          icon: <ClockCircleOutlined />,
        },
        {
          title: t('location.stopTime'),
          value: dateFormat(locationData?.stoptime || '').split(' ')[1],
          icon: <ClockCircleOutlined />,
        },
      ],
    ],
    [t],
  );

  useEffect(() => {
    setActivateDate((dataArray && dataArray[0])?.uniqueid || undefined);
  }, [dataArray]);

  return (
    <Drawer
      width="60%"
      placement="right"
      title={t('location.title')}
      onClose={onClose}
      open={isOpen}
      destroyOnClose
      bodyStyle={{ background: '#f0f0f0' }}
    >
      {dataArray && (
        <Radio.Group
          defaultValue={firstDate}
          size="large"
          style={{
            marginTop: 16,
            display: 'flex',
            gridGap: '10px',
            marginBottom: 16,
            flexWrap: 'wrap',
          }}
          onChange={e => setActivateDate(e.target.value)}
        >
          {dataArray?.map(item => {
            return (
              item.city && (
                <div>
                  <Radio.Button
                    key={item.uniqueid}
                    onClick={() => setActivateDate(item.uniqueid)}
                    value={item.uniqueid}
                  >
                    {item.city}
                  </Radio.Button>
                </div>
              )
            );
          })}
        </Radio.Group>
      )}

      {allStats(selectedCityData)?.map(statsRows => {
        const col = {
          lg: 12,
          xl: 12,
          xs: 24,
        };
        return (
          <Row
            key={`row-${statsRows?.[0]?.title}`}
            gutter={[12, 12]}
            style={{ marginBottom: 12 }}
          >
            {statsRows?.map(({ title, value, icon }) => (
              <Col key={value} {...col}>
                <StatCard title={title} value={value ?? '-'} icon={icon} />
              </Col>
            ))}
          </Row>
        );
      })}
      <CoverageMap
        allowCoverage
        mapHeight={480}
        mapsZoom={16}
        dataProps={
          selectedCityData?.latitude &&
          selectedCityData?.longitude && [
            {
              id: 'antenna-location',
              lat: Number(selectedCityData.latitude),
              lng: Number(selectedCityData.longitude),
            },
          ]
        }
      />
    </Drawer>
  );
};

export default SessionLocationArray;
